import claimedPromotionFragment from '../store/fragments/claimed-promotion';
import { getHardCodedData, removeTypename, generateOfferRoute } from '../utils';
import { idLens, nameLens, codeLens } from '../data';
import { view } from 'ramda';
import { SUBMIT } from '@updater-core/lib/tracker';
import { RECEIVE, DISPLAY } from '@updater-core/lib/tracker';
import { formatPromotionForEventStream } from '../eventstream';
import gql from 'graphql-tag';

export const claimPromotionMutation = gql`
  mutation ClaimPromotion($input: ClaimPromotionInput!) {
    claimPromotion(input: $input) {
      claimedPromotion {
        ...${claimedPromotionFragment.name}
      }
    }
  }
  ${claimedPromotionFragment.fragment}
`;

export const claimDeals = ({
  promotions,
  numSelectedOffers,
  router,
  selectedOffers,
  tracker,
  store,
}) => {
  if (numSelectedOffers < 1) {
    return;
  }

  promotions.map((promo) => {
    const location = promo.validFor[0];
    if (isOfferSelected(promo.promotion, location, selectedOffers)) {
      const input = {
        promotionId: promo.promotion.id,
        entityId: location.id,
        contactPreference: {
          type: 'EMAIL',
        },
      };

      const event = {
        verb: SUBMIT,
        object: 'Unlock Deal',
        details: {
          promotionId: promo.promotion.id,
          contactPreference: {
            type: 'EMAIL',
          },
          entityId: location.id,
        },
      };

      tracker.track(event);

      store.mutate({
        mutation: claimPromotionMutation,
        variables: {
          input,
        },
      });
    } else {
      console.log('not claiming', promo.promotion, location);
    }
  });

  router.goTo(`/claimed/${numSelectedOffers}`);
};

export function claimPromotion(
  store,
  tracker,
  entityId,
  entityName,
  promotionId,
  promotionCode,
  source
) {
  const input = {
    source,
    promotionId,
    entityId,
    promotionCode,
  };

  const event = {
    verb: SUBMIT,
    object: 'unlock_deal',
    details: {
      contactPreference: false,
      code: promotionCode,
      sourceId:
        source === 'exclusive_offers' ? 1 : source === 'splash_page' ? 2 : null,
      entityName,
      ...input,
    },
  };

  tracker.track(event);

  return store
    .mutate({
      mutation: claimPromotionMutation,
      variables: {
        input,
      },
    })
    .then(
      ({
        data: {
          claimPromotion: { claimedPromotion },
        },
      }) => {
        trackImpressions(tracker.track, [], [claimedPromotion]);
        trackReceivedOffers(tracker.track, [], [claimedPromotion]);
        return claimedPromotion;
      }
    );
}

export function getOfferId(promo, location) {
  return `${promo.id}:${location.id}`;
}

export function goToTerms(promo, router) {
  if (promo.__typename === 'EligiblePromotion') {
    const entity = promo.validFor[0];
    const entityType = entity.__typename.toLowerCase();
    router.goTo(
      generateOfferRoute('confirm', promo.promotion.id, entityType, entity.id)
    );
  } else {
    router.goTo(`/unlocked/${promo.id}`);
  }
}

export function isOfferSelected(promo, location, selectedOffers = {}) {
  const selectedState = selectedOffers[getOfferId(promo, location)];
  // offers default to selected so until the state as been tracked they are
  // selected
  return selectedState === undefined ? true : selectedState;
}

export function parsePromotions(eligiblePromotions) {
  return eligiblePromotions.reduce(
    (promos, promo) => {
      if (promo.promotion.metadata.exclusive) {
        promos.exclusive.push(promo);
      }
      return promos;
    },
    {
      exclusive: [],
    }
  );
}

export const sortExclusiveOffers = (offers) =>
  offers.sort((A, B) => {
    const AData = getHardCodedData(A.promotion || A);
    const BData = getHardCodedData(B.promotion || B);
    if (
      !AData ||
      !BData ||
      AData.rank == undefined ||
      BData.rank == undefined
    ) {
      return 0;
    }

    return AData.rank - BData.rank;
  });

export const totalPromotionValue = (promos = [], claimedPromos = []) => {
  const addPromotionValue = (acc, { promotion }) =>
    acc + getHardCodedData(promotion).value || 0;
  const value =
    promos.reduce(addPromotionValue, 0) +
    claimedPromos.reduce(addPromotionValue, 0);
  if (!value) {
    return '';
  }

  return `$${value}`;
};

export function trackReceivedOffers(
  track,
  eligiblePromotions = [],
  claimedPromotions = []
) {
  track({
    verb: RECEIVE,
    object: 'offers',
    details: {
      source: 'exclusive_offers',
      claimedPromotions: claimedPromotions.length
        ? removeTypename(claimedPromotions).map(
            (claimedPromotion) => claimedPromotion.id
          )
        : [],
      eligiblePromotions: eligiblePromotions.length
        ? removeTypename(eligiblePromotions).map(({ promotion, validFor }) => ({
            exclusive: promotion.metadata.exclusive,
            promotionId: promotion.id,
            validFor: validFor.map((x) => x.id),
          }))
        : [],
    },
  });
}

export function trackImpressions(
  track,
  exclusive = [],
  claimedPromotions = []
) {
  exclusive.forEach((promotion) => {
    promotion.validFor.forEach((entity) => {
      const event = {
        verb: DISPLAY,
        object: 'exclusive_offer',
        segmentEventName: 'Viewed Promotions Exclusive Offer',
        details: {
          promotionId: view(idLens, promotion.promotion),
          promotionCode: view(codeLens, promotion.promotion),
          entityId: view(idLens, entity),
          entityName: view(nameLens, entity),
          promotion: formatPromotionForEventStream(promotion.promotion, entity),
          source: 'exclusive_offers',
        },
      };

      track(event);
    });
  });
  claimedPromotions.forEach((promotion) => {
    track({
      verb: DISPLAY,
      object: 'exclusive_offer',
      segmentEventName: 'Viewed Promotions Claimed Offer',
      details: {
        promotionId: view(idLens, promotion.promotion),
        entityId: view(idLens, promotion.claimedFor),
        entityName: view(nameLens, promotion.claimedFor),
        promotionCode: view(codeLens, promotion.promotion),
        source: 'exclusive_offers',
      },
    });
  });
}
